import { Component, Input, OnInit } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Agent } from '../agent-builder-services/agent.service';
import { KnowledgeBaseService } from '../agent-builder-services/knowledge-base.service';
import { LanguageServices } from 'src/app/services/LanguageServices';

/** um chunk gravado com status "candidate" na propria KB */
interface Candidato {
    _id?: string;
    correlationId?: string;
    chunk_id?: string;
    text?: string;
    source?: string;
    created_at?: any;
}

/** os chunks de um mesmo correlationId: um documento proposto */
interface DocumentoCandidato {
    correlationId: string;
    /** o texto inteiro, remontado a partir dos chunks */
    texto: string;
    source: string;
    criadoEm: any;
    quantidadeDeChunks: number;
}

/**
 * Curadoria dos documentos que o agente propos.
 *
 * Nada aqui esta ativo na base: sao chunks com status "candidate", que o
 * searchKb nao recupera. Aprovar e o unico caminho para o conteudo passar a
 * ser usado nas respostas.
 */
@Component({
    selector: 'agent-builder-candidates',
    templateUrl: './agent-builder-candidates.component.html',
    styleUrls: ['./agent-builder-candidates.component.scss'],
    standalone: false
})
export class AgentBuilderCandidatesComponent implements OnInit {

    @Input() agent: Agent;

    documentos: DocumentoCandidato[] = [];
    carregando = false;

    // ---- revisao ----
    modalAberto = false;
    salvando = false;
    documentoEmRevisao: DocumentoCandidato = null;
    textoRevisado = '';

    constructor(
        private kbService: KnowledgeBaseService,
        private message: NzMessageService,
        private modal: NzModalService,
        private ls: LanguageServices
    ) {}

    ngOnInit(): void {
        this.carregar();
    }

    carregar(): void {

        if (this.agent == null || this.agent._id == null) {
            return;
        }

        if (this.agent.kbTable == null || this.agent.kbTable.length === 0) {
            this.documentos = [];
            return;
        }

        this.carregando = true;

        this.kbService.getCandidatos(this.agent).subscribe({
            next: (retorno) => {
                this.documentos = this.agruparPorDocumento(this.extrairLista(retorno));
                this.carregando = false;
            },
            error: (erro) => {
                console.error('Erro ao carregar os candidatos:', erro);
                this.documentos = [];
                this.carregando = false;
                this.message.error(this.ls.instant('agentBuilder.candidates.loadError'));
            }
        });
    }

    private extrairLista(retorno: any): Candidato[] {

        if (retorno == null) {
            return [];
        }

        if (Array.isArray(retorno)) {
            return retorno as Candidato[];
        }

        if (Array.isArray(retorno.data)) {
            return retorno.data as Candidato[];
        }

        return [];
    }

    /**
     * Remonta o documento a partir dos chunks.
     *
     * O texto foi picado na ingestao; para revisar, a pessoa precisa ver o
     * conteudo inteiro, e nao pedaco por pedaco. A ordem vem do sufixo do
     * chunk_id, que o upsertChunks monta como "<docId>:<indice>".
     */
    private agruparPorDocumento(chunks: Candidato[]): DocumentoCandidato[] {

        let porCorrelacao = new Map<string, Candidato[]>();

        for (let chunk of chunks) {

            let chave = chunk.correlationId;

            if (chave == null || chave.length === 0) {
                continue;
            }

            if (!porCorrelacao.has(chave)) {
                porCorrelacao.set(chave, []);
            }

            porCorrelacao.get(chave).push(chunk);
        }

        let resultado: DocumentoCandidato[] = [];

        porCorrelacao.forEach((lista, correlationId) => {

            lista.sort((a, b) => this.indiceDoChunk(a) - this.indiceDoChunk(b));

            let partes: string[] = [];

            for (let chunk of lista) {
                partes.push(chunk.text ?? '');
            }

            resultado.push({
                correlationId: correlationId,
                texto: partes.join('\n\n'),
                source: lista[0]?.source ?? '',
                criadoEm: lista[0]?.created_at,
                quantidadeDeChunks: lista.length
            });
        });

        return resultado;
    }

    /** o chunk_id termina em ":<indice>"; sem isso a ordem seria a do banco */
    private indiceDoChunk(chunk: Candidato): number {

        let id = chunk.chunk_id ?? '';
        let posicao = id.lastIndexOf(':');

        if (posicao < 0) {
            return 0;
        }

        let numero = parseInt(id.substring(posicao + 1), 10);

        return isNaN(numero) ? 0 : numero;
    }

    // ------------------------------------------------------------------
    // Revisao
    // ------------------------------------------------------------------

    revisar(documento: DocumentoCandidato): void {
        this.documentoEmRevisao = documento;
        this.textoRevisado = documento.texto;
        this.modalAberto = true;
    }

    fecharModal(): void {
        this.modalAberto = false;
    }

    aprovar(): void {

        if (this.textoRevisado == null || this.textoRevisado.trim().length === 0) {
            this.message.warning(this.ls.instant('agentBuilder.candidates.emptyText'));
            return;
        }

        this.salvando = true;

        this.kbService.aprovarCandidato(
                this.agent,
                this.documentoEmRevisao.correlationId,
                this.textoRevisado).then(()=>{

            this.salvando = false;
            this.modalAberto = false;
            this.message.success(this.ls.instant('agentBuilder.candidates.approved'));
            this.carregar();

        }).catch((erro)=>{
            this.salvando = false;
            console.error('Erro ao aprovar o candidato:', erro);
            this.message.error(this.ls.instant('agentBuilder.candidates.approveError'));
        });
    }

    confirmarRecusa(documento: DocumentoCandidato): void {

        this.modal.confirm({
            nzTitle: this.ls.instant('agentBuilder.candidates.rejectTitle'),
            nzContent: this.ls.instant('agentBuilder.candidates.rejectHint'),
            nzOkDanger: true,
            nzOnOk: () => this.recusar(documento)
        });
    }

    private recusar(documento: DocumentoCandidato): void {

        this.kbService.recusarCandidato(this.agent, documento.correlationId).subscribe({
            next: () => {
                this.message.success(this.ls.instant('agentBuilder.candidates.rejected'));
                this.carregar();
            },
            error: (erro) => {
                console.error('Erro ao recusar o candidato:', erro);
                this.message.error(this.ls.instant('agentBuilder.candidates.rejectError'));
            }
        });
    }

    /** primeiras linhas do texto, para a pessoa reconhecer o documento na lista */
    previa(texto: string): string {

        if (texto == null) {
            return '';
        }

        let limpo = texto.trim();

        if (limpo.length <= 220) {
            return limpo;
        }

        return limpo.substring(0, 220) + '...';
    }
}
